import { isIdentifierRecalled, type IdentifierRecord } from '@myplayer/schema';
import { IconSort, IconSortAsc, IconSortDesc } from '@myplayer/ui';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { useMemo, useState, type ReactElement } from 'react';
import {
  Button,
  ProductsSearchFilterBar,
  Spinner,
  Tag,
  computeVirtualizedSlice,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildProductContextMenuItems } from './context-menu-items.js';
import { StatusBadge } from './product-detail-panel.js';
import {
  type DuplicateIdentifierConflict,
  formatPlainValidationError,
  resolveIdentifierDisplayStatus,
  resolveIdentifierLifecycleState,
} from './products-firestore.js';
import {
  computeProductFacetCounts,
  formatLastChangedDate,
  resolveAppliedScheduledChange,
  resolvePendingFiredScheduledChange,
  resolvePlainProductStatus,
  resolveRecordLastChangedIso,
  resolveRowDestinationLabel,
  resolveScheduledChange,
} from './products-helpers.js';
import { type ProductsTableProps } from './products-recall-helpers.js';
import { ADMIN_STRINGS } from './strings.js';

export * from './products-recall-helpers.js';
const columnHelper = createColumnHelper<IdentifierRecord>();
function buildProductsTableColumns(params: {
  readonly showWorkspaceBadges: boolean;
  readonly workspaceNameMap?: ReadonlyMap<string, string> | undefined;
  readonly duplicateConflicts?: ReadonlyMap<string, DuplicateIdentifierConflict> | undefined;
}) {
  return [
    columnHelper.accessor((row) => row.draft?.title ?? row.title ?? row.primaryKey, {
      id: 'title',
      header: 'Product',
      cell: (info) => {
        const rec = info.row.original;
        const brand = rec.draft?.brand ?? rec.brand;
        const wsName = rec.workspaceId
          ? (params.workspaceNameMap?.get(rec.workspaceId) ?? rec.workspaceId)
          : undefined;
        return (
          <div className="flex flex-col gap-0.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-fg">{info.getValue()}</span>
              {params.showWorkspaceBadges && wsName && (
                <Tag
                  variant="outline"
                  size="sm"
                  data-testid={`product-ws-badge-${rec.identifierKey}`}
                >
                  {wsName}
                </Tag>
              )}
            </div>
            <span className="font-mono text-[12px] text-fg-secondary tabular-nums">
              {brand ? `${brand} · ${rec.primaryKey}` : rec.primaryKey}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => resolveRowDestinationLabel(row), {
      id: 'destination',
      header: 'Where scans go',
      cell: (info) => {
        const rec = info.row.original;
        const firstErr = rec.validation?.errors?.[0];
        const recalled = isIdentifierRecalled(rec);
        const scheduled = resolveScheduledChange(rec);
        const pendingFired = resolvePendingFiredScheduledChange(rec);
        const appliedSchedule = resolveAppliedScheduledChange(rec);
        const conflict = params.duplicateConflicts?.get(
          `${rec.orgId ?? ''}::${rec.workspaceId ?? ''}::${rec.identifierKey}`,
        );
        return (
          <div className="min-w-0 space-y-0.5">
            <p
              className={`truncate font-mono text-[12px] ${recalled ? 'font-semibold text-status-error' : 'text-fg'}`}
            >
              {info.getValue()}
            </p>
            {pendingFired !== null && !recalled && (
              <p
                data-testid={`table-switching-note-${rec.identifierKey}`}
                className="truncate text-[11px] font-medium text-status-warning"
              >
                Switching now to {pendingFired.targetHref}
              </p>
            )}
            {pendingFired === null && scheduled !== null && !recalled && (
              <p className="truncate text-[11px] font-medium text-fg-secondary">
                Scheduled: switches to {scheduled.targetHref} on {scheduled.formattedDate}
              </p>
            )}
            {pendingFired === null &&
              scheduled === null &&
              appliedSchedule !== null &&
              !recalled && (
                <p className="truncate text-[11px] font-medium text-fg-secondary">
                  Applied on {appliedSchedule.formattedDate}
                </p>
              )}
            {firstErr !== undefined && (
              <p
                data-testid={`table-validation-error-${rec.identifierKey}`}
                className="text-[12px] font-medium text-status-error"
              >
                {formatPlainValidationError(firstErr)}
              </p>
            )}
            {conflict !== undefined && (
              <p
                data-testid={`table-duplicate-conflict-${rec.identifierKey}`}
                className="text-[12px] font-medium text-status-error"
              >
                {conflict.message}
              </p>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => resolvePlainProductStatus(row), {
      id: 'status',
      header: 'Status',
      cell: (info) => {
        const rec = info.row.original;
        const conflict = params.duplicateConflicts?.get(
          `${rec.orgId ?? ''}::${rec.workspaceId ?? ''}::${rec.identifierKey}`,
        );
        return (
          <StatusBadge
            status={conflict ? 'invalid' : resolveIdentifierDisplayStatus(rec)}
            lifecycleState={resolveIdentifierLifecycleState(rec)}
            plainStatus={info.getValue()}
            recalled={isIdentifierRecalled(rec)}
            pendingFired={resolvePendingFiredScheduledChange(rec) !== null}
          />
        );
      },
    }),
    columnHelper.accessor((row) => resolveRecordLastChangedIso(row) ?? '', {
      id: 'lastChanged',
      header: 'Last changed',
      cell: (info) => (
        <span className="font-medium text-fg tabular-nums">
          {formatLastChangedDate(info.getValue() || undefined)}
        </span>
      ),
    }),
  ];
}
export function ProductsTable(props: ProductsTableProps): ReactElement {
  const brandFilter = props.brandFilter ?? 'ALL';
  const workspaceFilter = props.workspaceFilter ?? 'ALL';
  const showWorkspaceBadges = props.showWorkspaceBadges ?? false;
  const sortBy = props.sortBy ?? 'MODIFIED_DESC';
  const viewMode = props.viewMode ?? 'table';
  const showSearchBar = props.showSearchBar ?? false;
  const loading = props.loading ?? false;
  const [sorting, setSorting] = useState<SortingState>([]);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(640);
  const { bindContextMenu, contextMenuElement } = useContextMenu();
  const facetCounts = useMemo(
    () => props.facetCounts ?? computeProductFacetCounts(props.records),
    [props.facetCounts, props.records],
  );
  const columns = useMemo(
    () =>
      buildProductsTableColumns({
        showWorkspaceBadges,
        workspaceNameMap: props.workspaceNameMap,
        duplicateConflicts: props.duplicateConflicts,
      }),
    [showWorkspaceBadges, props.workspaceNameMap, props.duplicateConflicts],
  );
  const table = useReactTable({
    data: props.records as IdentifierRecord[],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const allRows = table.getRowModel().rows;
  const virtualSlice = computeVirtualizedSlice({
    items: allRows,
    scrollTop,
    viewportHeight,
    rowHeight: 54,
  });
  const hasActiveFilters =
    props.searchQuery.trim().length > 0 ||
    brandFilter !== 'ALL' ||
    props.statusFilter !== 'ALL' ||
    props.sectorFilter !== 'ALL' ||
    props.validationFilter !== 'ALL' ||
    workspaceFilter !== 'ALL';
  const renderSpacer = (h: number): ReactElement | null =>
    h > 0 ? (
      <tr aria-hidden="true">
        <td colSpan={columns.length} style={{ height: `${h}px`, padding: 0 }} />
      </tr>
    ) : null;
  return (
    <div className="min-w-0 space-y-3" data-testid="products-table-card">
      {(showSearchBar || hasActiveFilters) && (
        <ProductsSearchFilterBar
          idPrefix="filter-product"
          ariaLabel="Products search and filters"
          searchId="search-product-gtin"
          searchQuery={props.searchQuery}
          onSearchChange={props.setSearchQuery}
          brandFilter={brandFilter}
          setBrandFilter={props.setBrandFilter}
          statusFilter={props.statusFilter}
          setStatusFilter={props.setStatusFilter}
          sectorFilter={props.sectorFilter}
          setSectorFilter={props.setSectorFilter}
          validationFilter={props.validationFilter}
          setValidationFilter={props.setValidationFilter}
          workspaceFilter={workspaceFilter}
          setWorkspaceFilter={props.setWorkspaceFilter}
          availableWorkspaces={props.availableWorkspaces}
          sortBy={sortBy}
          onSortChange={(next) => {
            props.setSortBy?.(next);
            setSorting([]);
          }}
          viewMode={viewMode}
          setViewMode={props.setViewMode}
          facetCounts={facetCounts}
          onFilterChangeResetScroll={() => setScrollTop(0)}
        />
      )}

      {/* Single flush table card (no boxes within boxes) */}
      <div className="overflow-hidden rounded-card border border-border bg-surface shadow-card-base">
        <div
          data-testid="products-virtual-scroll-container"
          onScroll={(e) => {
            setScrollTop(e.currentTarget.scrollTop);
            if (e.currentTarget.clientHeight > 0) setViewportHeight(e.currentTarget.clientHeight);
          }}
          className="max-h-[calc(100vh-220px)] overflow-auto"
        >
          <table
            aria-label="Products table"
            className="w-full border-collapse text-left text-[13px]"
          >
            <thead className="sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="border-b border-subtle bg-surface-subtle text-[12px] text-fg-secondary"
                >
                  {headerGroup.headers.map((header) => {
                    const sortDir = header.column.getIsSorted();
                    return (
                      <th key={header.id} className="px-4 py-2.5 font-semibold">
                        {header.isPlaceholder ? null : header.column.getCanSort() ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="inline"
                            onClick={header.column.getToggleSortingHandler()}
                            className="inline-flex cursor-pointer items-center gap-1 text-left font-semibold text-fg-secondary hover:text-fg"
                          >
                            <span className="sr-only">
                              Sort by {String(header.column.columnDef.header)}
                            </span>
                            <span aria-hidden="true">
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                            {sortDir === 'asc' ? (
                              <IconSortAsc />
                            ) : sortDir === 'desc' ? (
                              <IconSortDesc />
                            ) : (
                              <IconSort className="opacity-50" />
                            )}
                          </Button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-subtle">
              {loading ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    role="status"
                    data-testid="products-loading-state"
                    className="py-10 text-center text-[13px] text-fg-secondary"
                  >
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Spinner size={22} />
                      <span>{ADMIN_STRINGS.loading.products}</span>
                    </div>
                  </td>
                </tr>
              ) : allRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    data-testid="products-table-empty-state"
                    className="py-10 text-center text-[13px] text-fg-secondary"
                  >
                    <p data-testid="products-empty-state">{ADMIN_STRINGS.empty.productsTable}</p>
                  </td>
                </tr>
              ) : (
                <>
                  {renderSpacer(virtualSlice.topSpacerHeight)}
                  {virtualSlice.visibleItems.map((row) => {
                    const rec = row.original;
                    const isSelected = rec.identifierKey === props.selectedIdentifierKey;
                    const selectRow = (): void => props.onSelectRecord(rec.identifierKey);
                    return (
                      <tr
                        key={row.id}
                        tabIndex={0}
                        aria-selected={isSelected}
                        data-testid={`table-row-${rec.identifierKey}`}
                        onClick={selectRow}
                        onContextMenu={bindContextMenu(() =>
                          buildProductContextMenuItems({
                            record: rec,
                            effectiveResolverHost: props.effectiveResolverHost,
                            canEdit: props.canEdit,
                            onSelectRecord: props.onSelectRecord,
                            onEditRecord: props.onEditRecord,
                          }),
                        )}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            selectRow();
                          }
                        }}
                        className={`h-[54px] cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand ${isSelected ? 'bg-surface-subtle text-fg' : 'bg-surface hover:bg-surface-subtle'}`}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="px-4 py-2.5 align-middle">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {renderSpacer(virtualSlice.bottomSpacerHeight)}
                </>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-subtle bg-surface px-4 py-2.5 text-[12px] text-fg-secondary tabular-nums">
          <span data-testid="products-pagination-summary">
            Showing {virtualSlice.visibleItems.length} of {props.records.length} products
          </span>
        </div>
      </div>
      {contextMenuElement}
    </div>
  );
}
